<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <!-- Dynamic Parent -->
      <router-link v-if="fromNotifications" :to="{ name: 'ViewPendingNotifications', query: { entityType: route.query.entityType, statusInd: route.query.statusInd } }"
        class="mb-0 main-identifier fw-semibold text-decoration-none">
        Notifications
      </router-link>

      <router-link v-else :to="{ name: 'ViewPaymentVouchers' }" class="mb-0 main-identifier fw-semibold text-decoration-none" title="Go back to Payment Vouchers">
        Payment Vouchers
      </router-link>
      <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">View Payment Voucher</p>
    </div>

    <div class="d-flex justify-content-center mb-5">

      <div class="form-outer-wrapper">
        <div class="form-outer">
          <form>
            <div class="crud-form position-relative">

              <div class="form-inner mb-2">
                <div class="form-header d-flex justify-content-between align-items-center">
                  <h1 class="mb-0">Payment Voucher</h1>
                </div>

                <div class="form-content-area position-relative">
                  <div v-if="loadingDetails" class="loading-overlay text-center">
                    <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
                    <p class="mt-2 mb-0 text-muted">Loading payment voucher details...</p>
                  </div>

                  <div v-if="errorMessage && !loadingDetails" class="alert alert-warning" role="alert">
                    {{ errorMessage }}
                  </div>
                  <!-- Header Info -->
                  <div class="row mb-3">
                    <div class="pv-header mt-3">
                      <h5 class="mda-title ms-3 mb-0 text-start">PV No: <span class="pv-no">{{ pvInfo.number ||
                        'N/A' }}</span></h5>
                      <h5 class="mda-title mb-0 text-center">Warrant No: <span class="warrant-no">{{ warrantInfo.number
                        ||
                        'N/A' }}</span></h5>
                      <h5 class="mda-title me-3 mb-0 text-end">{{ mdaInfo.name || 'N/A' }}</h5>
                    </div>
                  </div>

                  <!-- Payment Info -->
                  <div class="row mb-3 payment-item-wrapper justify-content-center">
                    <div class="row content-wrapper section-border h-100 w-100">
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label align-items-center">Beneficiary</label>
                          <p type="text" class="form-control view-only-input text-center" readonly>
                            {{ form.beneficiary || 'N/A' }}
                          </p>
                        </div>
                      </div>
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Initiated Date</label>
                          <p type="text" class="form-control view-only-input text-center" readonly>
                            {{ formatInitDate(form.pvInitDate) || 'N/A' }}
                          </p>
                        </div>
                      </div>
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Bank</label>
                          <p type="text" class="form-control view-only-input text-center" readonly>
                            {{ form.bank || 'N/A' }}
                          </p>
                        </div>
                      </div>
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Gross Amount</label>
                          <input type="text" class="form-control view-only-input text-center"
                            :value="'₦' + form.grossAmount || 0" readonly>
                        </div>
                      </div>
                      <div class="col d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Total Deductions</label>
                          <input type="text" class="form-control view-only-input text-center"
                            :value="'₦' + form.deductions || 0" readonly>
                        </div>
                      </div>
                      <div class="col no-border d-flex">
                        <div class="d-flex flex-column justify-content-center h-100 w-100">
                          <label class="form-label fw-bold fixed-label">Net Amount</label>
                          <input type="text" class="form-control view-only-input text-center"
                            :value="'₦' + form.netAmount || 0" readonly>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Payment Info -->
                  <div class="row mb-3 mt-4">
                    <div class="col-md-12">
                      <h5 class="mda-title mb-0 text-center fw-semibold">Particulars: <span class="particulars">{{
                        form.particulars ||
                        'N/A' }}</span></h5>
                    </div>
                  </div>

                  <div v-if="String(form.paymentType).toLowerCase() === 'cheque' && form.chequeNumber" class="row mb-3">
                    <div class="col-md-12">
                      <h5 class="mda-title mb-0 text-center fw-semibold">Cheque Number: <span class="particulars">{{
                        form.chequeNumber }}</span></h5>
                    </div>
                  </div>

                  <div v-if="isReturnedPV && returnRemarkDetails" class="row mb-3 mt-2">
                    <div class="col-md-12">
                      <div class="alert alert-warning mb-0">
                        <p class="mb-1 fw-semibold">Returned for correction</p>
                        <p class="mb-1"><strong>{{ returnRemarkDetails.label }}:</strong></p>
                        <p class="mb-0">{{ returnRemarkDetails.value }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Deductions -->
                  <div class="row mb-3 payment-item-wrapper justify-content-center">
                    <div class="table-responsive">
                      <h5 class="mda-title ms-1 mb-2 text-start">Deductions</h5>
                      <table class="table table-bordered text-center align-middle">
                        <thead class="table-light">
                          <tr>
                            <th>Description</th>
                            <!-- <th>Percentage</th> -->
                            <th>Amount (₦)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="deductions.length === 0">
                            <td colspan="3" class="text-muted">No deductions added</td>
                          </tr>
                          <tr v-for="(item, index) in deductions" :key="index"
                            :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                            <td>
                              {{ item.description }}
                            </td>
                            <!-- <td>
                            {{ item.percentage > 0 ? item.percentage + '%' : '-' }}
                          </td> -->
                            <td>
                              {{ Number(item.amountReleased || 0).toLocaleString() }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Economic Line Items -->
                    <div class="table-responsive mt-4">
                      <h5 class="mda-title ms-1 mb-2 text-start">Economic Line Items</h5>
                      <table class="table table-bordered text-center align-middle">
                        <thead class="table-light">
                          <tr>
                            <th>Eco Code</th>
                            <th>Title</th>
                            <th>Amount Budgeted (₦)</th>
                            <th>Amount Released (₦)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-if="economicItems.length === 0">
                            <td colspan="4" class="text-muted">No economic line items added</td>
                          </tr>
                          <tr v-for="(item, index) in economicItems" :key="index"
                            :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                            <td>
                              {{ item.ecoCode }}
                            </td>
                            <td>
                              {{ item.title }}
                            </td>
                            <td>
                              {{ Number(item.amountBudgeted || 0).toLocaleString() }}
                            </td>
                            <td>
                              {{ Number(item.amountReleased || 0).toLocaleString() }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                </div>

                <!-- Submit Button -->
                <div v-if="!loadingDetails" class="d-flex justify-content-end mt-4">
                  <div class="d-flex justify-content-end">
                    <button v-if="canEditReturnedPV" type="button" class="btn btn-warning me-2"
                      @click="goToCorrectionPage">
                      Edit Returned Voucher
                    </button>
                    <button v-if="canGoToApproval && !errorMessage" type="button" class="btn btn-primary me-2" @click="goToApprovalPage">
                      Go to Approval
                    </button>
                    <button v-if="canDownloadPV" type="button" class="btn btn-outline-primary export-btn-app me-2"
                      @click="downloadPV" :disabled="loadingPV" aria-live="polite"
                      :aria-label="loadingPV ? 'Downloading...' : 'Download Payment Voucher'">
                      <span v-if="loadingPV" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ loadingPV ? 'Downloading...' : 'Download Payment Voucher' }}
                    </button>
                    <button v-if="canCancelPV" type="button" class="btn btn-outline-danger me-2"
                      @click="cancelPV" :disabled="cancellingPV">
                      <span v-if="cancellingPV" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      {{ cancellingPV ? 'Cancelling...' : 'Cancel PV' }}
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import '@/assets/css/dashboard.css'
import '@/assets/css/responsive-table.css'
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';


const root_api_url = process.env.VUE_APP_API_ROOT_URL
const fromNotifications = computed(() => route.query.from === 'ViewPendingNotifications'  && route.query.entityType && route.query.statusInd)

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const id = route.params.id || route.query.id
const loadingPV = ref(false)
const cancellingPV = ref(false)
const loadingDetails = ref(false)
const errorMessage = ref('')
const hasRole = (role) => !!(userStore.profile?.role || {})[role]
const canGoToApproval = computed(() => {
  return Boolean(id) && (
    hasRole('dirCAD') ||
    hasRole('dirOfPayment') ||
    hasRole('dirOfTreasury') ||
    hasRole('acctGeneral')
  )
})

const isReturnedPV = computed(() => {
  const pv = pvPayload.value || {}
  return pv.returnedPVInd === 1 || pv.returned === true
})

const isCompletedPV = computed(() => {
  const pv = pvPayload.value || {}
  return pv.completedPVInd === 1 || pv.completed === true
})

const isCancelledPV = computed(() => {
  const pv = pvPayload.value || {}
  return pv.cancelledInd === 1 || pv.cancelled === true
})

const isReturnPVToInitiator = computed(() => {
  const pv = pvPayload.value || {}
  return pv.returnPVToInitiator === true
})

const returnRemarkDetails = computed(() => {
  const pv = pvPayload.value || {}

  if (pv.acctGeneralRemarks) {
    return { label: 'Accountant General Remark', value: pv.acctGeneralRemarks }
  }

  if (pv.dirOfTreasuryRemarks) {
    return { label: 'Director of Treasury Remark', value: pv.dirOfTreasuryRemarks }
  }

  if (pv.dirCadRemarks) {
    return { label: 'DCAD Remark', value: pv.dirCadRemarks }
  }

  if (pv.dirOfPaymentRemarks) {
    return { label: 'Director of Payment Remark', value: pv.dirOfPaymentRemarks }
  }

  return null
})

const canEditReturnedPV = computed(() => {
  return Boolean(id) && isReturnPVToInitiator.value && !isCompletedPV.value && (hasRole('pvInitiator') || hasRole('initiator'))
})


// Reactive form data
const form = reactive({
  id: '',
  originatingMda: '',
  approvalAuthority: '',
  natureOfExpenditure: '',
  amountApproved: '',
  purposeOfRequest: '',
  approvedBudgetAmount: '',
  amtReleasedToDate: '',
  balance: '',
  performance: '',
  quarter: '',
  budgetYear: '',
  warrantInitiatedDate: '',
  noOfAppLimitPerQtr: '',
  noOfAuthIssuedInQtr: '',
  noOfAuthLeftInQtr: '',
  qtrAuthLimitAmt: '',
  approvedRecurrentAmount: '',
  approvedCapitalAmount: '',
  approvalBalance: '',
  evalRemarks: '',
  amtRecommended: '',
  sectoralHeadRemarks: '',
  dopRemarks: '',
  dotRemarks: '',
  commissionerRemarks: '',
  orgCode: '',
  permSecRemarks: '',
  warrantInitiatorId: '',
  sectoralHeadId: '',
  sectoralHeadRemarkDate: '',
  dopId: '',
  dopRemarkDate: '',
  permSecId: '',
  permSecRemarksDate: '',
  commissionerId: '',
  commRemarkDate: '',
  issueDate: '',
  approvalAuthorityId: '',
  mdaId: '',
  evalApprovedRecurrentAmount: '',
  evalApprovedCapitalAmount: '',
  evalApprovalBalance: '',
  groupTypeId: '',
  memoFileNumber: '',
  particulars: '',
  pvInitDate: '',
  paymentType: '',
  chequeNumber: '',
})

const deductions = ref([])
const economicItems = ref([])

const mdaInfo = reactive({
  name: ''
})

const warrantInfo = reactive({
  number: '',
  id: ''
})

const pvInfo = reactive({
  number: ''
})

const pvPayload = ref(null)

const resolvedWarrantId = computed(() => {
  const pv = pvPayload.value || {}
  return (
    pv.warrantId ||
    pv.warrantUUID ||
    pv.warrantUuid ||
    pv.warrantID ||
    ''
  )
})

const resolvedPvRef = computed(() => {
  const pv = pvPayload.value || {}
  return pv.id || id || ''
})

const resolvedPvNumber = computed(() => {
  const pv = pvPayload.value || {}
  return pv.pvNumber || pvInfo.number || ''
})

const canDownloadPV = computed(() => {
  const pv = pvPayload.value || {}
  const isCompleted = pv.completedPVInd === 1 || pv.completed === true
  return isCompleted && !isCancelledPV.value && hasRole('pvInitiator')
})

const isSuperAdmin = computed(() => {
  return String(userStore.profile?.roleName || '').toLowerCase().includes('super admin')
})

const canCancelPV = computed(() => {
  return Boolean(id) && isCompletedPV.value && !isCancelledPV.value && (hasRole('dirOfPayment') || isSuperAdmin.value)
})

const fetchWarrantEconomicItems = async (warrantId) => {
  if (!warrantId) return

  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/appraisal/warrant/${warrantId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    economicItems.value = (data.warrantEconomicLineItems || []).map(dto => ({
      id: dto.id,
      ecoCode: dto.ecoCode,
      title: dto.title,
      amountBudgeted: dto.amountBudgeted,
      amountReleased: dto.amountReleased,
    }))
  } catch (err) {
    console.error('Error loading warrant economic items:', err)
  }
}

const fetchPaymentVoucher = async () => {
  if (!id) {
    return
  }

  loadingDetails.value = true
  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/payment/voucher/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    pvPayload.value = data

    form.beneficiary = data?.payee || ''
    form.pvInitDate = data?.pvInitDate || ''
    form.paymentDate = data?.paymentDate || ''
    form.bank = data?.bankName || ''
    form.grossAmount = Number(data?.grossAmount || 0).toLocaleString()
    form.deductions = Number((data?.deductions || []).reduce((sum, item) => sum + Number(item?.amount || 0), 0)).toLocaleString()
    form.netAmount = Number(data?.netAmount || 0).toLocaleString()
    form.particulars = data?.particulars || form.particulars
    form.natureOfExpenditure = data?.natureOfExpenditure || form.natureOfExpenditure
    form.paymentType = data?.paymentType || ''
    form.chequeNumber = data?.chequeNumber || ''

    pvInfo.number = data?.pvNumber || data?.voucherNumber || 'N/A'
    warrantInfo.number = data?.warrantNumber || data?.warrantNo || data?.warrantId || 'N/A'
    warrantInfo.id = data?.warrantId || data?.warrantUUID || data?.warrantUuid || data?.warrantID || ''
    mdaInfo.name = data?.mdaName || data?.mda || data?.originatingMda || 'N/A'

    deductions.value = (data?.deductions || []).map(item => ({
      deduction: item?.dedTypeName || 'N/A',
      description: item?.dedTypeName || 'N/A',
      percentage: item?.percentage || 0,
      amountReleased: item?.amount || 0,
    }))

    // Fetch economic items from warrant
    if (warrantInfo.id) {
      await fetchWarrantEconomicItems(warrantInfo.id)
    }

  } catch (err) {
    console.error('Error loading payment voucher:', err)
    errorMessage.value = 'Failed to load payment voucher information. Please try again.'
  } finally {
    loadingDetails.value = false
  }
}

const downloadPV = async () => {
  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  if (!id) {
    await Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>Payment Voucher ID is missing.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  loadingPV.value = true
  const token = Cookies.get('auth_token')

  try {
    const response = await axios.get(`${root_api_url}/payment/pdf/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob'
    })

    let downloadName = 'payment_voucher.pdf' // default filename

    const disposition = response.headers['content-disposition']
    console.log('Content-Disposition header:', response.headers)

    if (disposition && disposition.includes('filename=')) {
      downloadName = disposition
        .split('filename=')[1]
        .replace(/"/g, '')
    }

    const blob = new Blob([response.data], { type: response.data.type || 'application/pdf' })
    const fileName = `${downloadName}`

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, fileName)
    } else {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  } catch (error) {
    if (error.response) {
      // Error body comes back as a Blob because of responseType: 'blob'
      let errorData = error.response.data
      if (errorData instanceof Blob) {
        try {
          errorData = JSON.parse(await errorData.text())
        } catch {
          errorData = {}
        }
      }

      await Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${errorData?.errorMessage || errorData?.error || 'Failed to download payment voucher.'}</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    } else {
      await Swal.fire({
        html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Unexpected error occurred. Kindly contact administrator</p>`,
        showConfirmButton: true,
        showCloseButton: true,
        icon: undefined,
      })
    }
  } finally {
    loadingPV.value = false
  }
}

const cancelPV = async () => {
  if (!id) return

  const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`

  const { value: reason } = await Swal.fire({
    title: 'Cancel Payment Voucher',
    input: 'textarea',
    inputLabel: 'Reason for cancellation',
    inputPlaceholder: 'Type cancellation reason here...',
    showCancelButton: true,
    confirmButtonText: 'Cancel PV',
    cancelButtonText: 'Close',
    preConfirm: (value) => {
      if (!value || !String(value).trim()) {
        Swal.showValidationMessage('Cancellation reason is required.')
      }
      return String(value || '').trim()
    },
  })

  if (!reason) return

  cancellingPV.value = true
  const token = Cookies.get('auth_token')

  try {
    await axios.post(`${root_api_url}/payment/voucher/${id}/cancel`, { reason }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    await Swal.fire({
      icon: 'success',
      title: 'Success',
      text: 'Payment Voucher cancelled successfully.'
    })
    await fetchPaymentVoucher()
  } catch (error) {
    await Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to cancel payment voucher.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    cancellingPV.value = false
  }
}

const goToApprovalPage = () => {
  if (!id) return
  router.push({ name: 'ApprovePaymentVoucher', params: { id } })
}

const goToCorrectionPage = () => {
  if (!id) return

  if (resolvedWarrantId.value) {
    router.push({
      name: 'ViewGeneratePVWithWarrant',
      params: { warrantId: String(resolvedWarrantId.value) },
      query: {
        pvId: String(resolvedPvRef.value),
        ...(resolvedPvNumber.value ? { pvNumber: String(resolvedPvNumber.value) } : {})
      }
    })
    return
  }

  router.push({
    name: 'ViewGeneratePVWithoutWarrant',
    query: {
      pvId: String(resolvedPvRef.value),
      ...(resolvedPvNumber.value ? { pvNumber: String(resolvedPvNumber.value) } : {})
    }
  })
}

const formatInitDate = (value) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const day = date.getDate()
  const month = date.toLocaleString('en-US', { month: 'long' })
  const year = date.getFullYear()

  const getOrdinal = (number) => {
    const remainder = number % 100
    if (remainder >= 11 && remainder <= 13) return `${number}th`
    switch (number % 10) {
      case 1:
        return `${number}st`
      case 2:
        return `${number}nd`
      case 3:
        return `${number}rd`
      default:
        return `${number}th`
    }
  }

  return `${getOrdinal(day)} ${month} ${year}`
}

onMounted(async () => {
  if (!userStore.profile) {
    await userStore.fetchProfile()
  }

  await fetchPaymentVoucher()
  document.title = "View Payment Voucher"
})


</script>

<style scoped>
.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: all;
  border-radius: 4px;
}

.view-only-input {
  border: none !important;
  background-color: transparent;
  padding-left: 0;
  cursor: default;
  pointer-events: none;
  color: #212529;
  font-size: 1rem;
  padding-right: 0;
  margin-right: 1rem;
  margin-bottom: 0.5rem !important;
  word-break: break-word;
  overflow-wrap: break-word;
}

.payment-item-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  /* forces every .col to match the tallest */
}

.payment-item-wrapper .view-only-input,
.payment-item-wrapper input.view-only-input {
  flex: 1;
  /* inputs (and your Approval‐Authority <div>) all grow equally */
  /* for your Approval Authority div: */
  white-space: pre-wrap;
}

.dashboard-layout .payment-item-wrapper {
  margin-top: 20px;
  padding: 0 20px;
}

.dashboard-layout .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 1rem;
}

.dashboard-layout .section-border {
  /* padding: 1rem; */
  border-top: 3px solid #667eea !important;
}


.dashboard-layout .payment-item-wrapper .col {
  /* padding: 0; */
  border-right: 1px solid var(--border);
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05); */
}

.dashboard-layout .payment-item-wrapper .col.no-border {
  border-right: none !important;
}

.dashboard-layout .payment-item-wrapper .fixed-label {
  min-height: 80px;
  justify-content: center;
  width: auto;
  box-sizing: border-box;
  text-align: center;
  /* all: unset; */
  display: flex;
  margin-bottom: 0;
  flex-shrink: 0;
  /* margin-left: 1rem; */
}


.dashboard-layout .payment-item-wrapper .content-wrapper {
  border: 1px solid var(--border);
  background-color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  /* padding: 1rem; */
}

.dashboard-layout .payment-item-wrapper .content {
  border-bottom: 2px solid var(--border);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  gap: 1rem;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

.dashboard-layout .payment-item-wrapper label {
  font-size: 16px;
  padding: .375rem 0 !important;
  margin-bottom: 0px !important;
  padding-bottom: 0.5rem;
  font-weight: 600;
}

.dashboard-layout .payment-item-wrapper .col-md-3.no-border {
  border: none;
}

.dashboard-layout .payment-item-wrapper .col-md-3.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .payment-item-wrapper .col-md-2.no-border {
  border: none;
}

.dashboard-layout .payment-item-wrapper .col-md-2.no-border label {
  padding: .375rem .75rem;
  margin-bottom: 0px !important;
}

.dashboard-layout .fixed-label {
  display: flex;
  align-items: center;
  /* vertical center */
  justify-content: center;
  /* horizontal center */
  min-height: 4rem;
  width: 100%;
  padding: 0 0.25rem;
  box-sizing: border-box;
  text-align: center;
}

/* Submit Button */
.btn-submit {
  background-color: #10b981;
  border-color: #10b981;
  color: #ffffff;
  padding: 0.625rem 3rem;
  font-weight: 500;
  border-radius: 4px;
  font-size: 1rem;
}

.btn-submit:hover {
  background-color: #059669;
  border-color: #059669;
  color: #ffffff;
}

.btn-submit:disabled {
  background-color: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
}

.pv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
</style>
